import React, { useState, useEffect } from "react";
import {
  FaSave,
  FaTimes,
  FaPlus,
  FaTrash,
  FaInfoCircle,
  FaFileImport,
} from "react-icons/fa";

// Auto-discover all topic templates from ../data/templates_*.json
// To add a new topic, just drop a templates_*.json file in the data directory.
const templateContext = require.context("../data", false, /^\.\/templates_.*\.json$/);
const TOPIC_TEMPLATES = {};
templateContext.keys().forEach((filename) => {
  const data = templateContext(filename);
  // Derive key from filename: "./templates_foo_bar.json" -> "foo_bar"
  const key = filename.replace(/^\.\/templates_/, "").replace(/\.json$/, "");
  TOPIC_TEMPLATES[key] = {
    label: data.sharedConfig?.topic?.name || key,
    data,
  };
});

// Move these components OUTSIDE the main component to prevent re-creation on every render
const Section = ({ title, children, info }) => (
  <div className="bg-gray-700 rounded-lg p-4 mb-4">
    <div className="flex items-center mb-3">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      {info && (
        <span className="ml-2 text-gray-400 text-sm flex items-center">
          <FaInfoCircle className="mr-1" /> {info}
        </span>
      )}
    </div>
    {children}
  </div>
);

const Input = ({ label, value, onChange, error, type = "text", placeholder, ...props }) => (
  <div className="mb-3">
    <label className="block text-gray-300 text-sm mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full p-2 bg-gray-600 text-white rounded border ${
        error ? "border-red-500" : "border-gray-500"
      } focus:border-blue-500 focus:outline-none`}
      {...props}
    />
    {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div className="mb-3">
    <label className="block text-gray-300 text-sm mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full p-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none"
    />
  </div>
);

const RangeInput = ({ label, value, onChange, min, max, step = 0.01 }) => (
  <div className="mb-3">
    <label className="block text-gray-300 text-sm mb-1">
      {label}: <span className="text-blue-400">{value}</span>
    </label>
    <input
      type="range"
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      min={min}
      max={max}
      step={step}
      className="w-full"
    />
  </div>
);

const ConditionEditor = ({ condition, onSave, onCancel, isNew }) => {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    topic: {
      name: "",
      description: "",
      proPosition: "",
      contraPosition: "",
      hashtags: [],
      talkingPoints: [],
    },
    agents: {
      count: 12,
      // Rigid per-condition settings (new schema)
      communicationStyle: "emotional",
      groupFocus: "neutral",
      // Stance distribution
      stanceDistribution: {
        pro: 0.5,
        contra: 0.5,
      },
      // Emotional frame pools
      emotionalFrames: {
        proStance: ["bonding", "hope", "validation"],
        contraStance: ["outrage", "fear", "concern"],
      },
      emotionalIntensity: 0.7,
      nameStyle: "realistic",
      generateBios: true,
      generateAvatars: true,
    },
    timing: {
      prePopulationPosts: 20,
      prePopulationComments: 30,
      postingIntervalMin: 8,
      postingIntervalMax: 15,
      reactionDelayMin: 10,
      reactionDelayMax: 120,
      reactionProbability: 0.4,
      commentProbability: 0.6,
    },
    session: {
      durationMinutes: 0,
      warningMinutes: 5,
    },
    completionRedirect: {
      url: "",
      participantIdParam: "i",
      includeParticipantId: true,
    },
    experimentalFactors: [],
    onboarding: {
      seedStatements: [],
      digestConfig: { maxExchanges: 3, preferFollowed: true },
      firstPostPrompt: "",
    },
  });

  const [errors, setErrors] = useState({});
  const [hashtagInput, setHashtagInput] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");

  // Get current topic templates
  const currentTopicData = selectedTopic ? TOPIC_TEMPLATES[selectedTopic]?.data : null;

  // Load template into form
  const loadTemplate = (templateId) => {
    if (!templateId || !currentTopicData) return;

    const template = currentTopicData.templates.find(t => t.id === templateId);
    if (!template) return;

    const shared = currentTopicData.sharedConfig;

    // Read experimental factors from the canonical `agents` block; fall back
    // to the legacy `factors` block for older topic templates. Fail loudly
    // if neither is present rather than silently defaulting to emo+neutral.
    const commStyle = template.agents?.communicationStyle || template.factors?.communicationStyle;
    const groupOri = template.agents?.groupFocus || template.factors?.groupOrientation;
    if (!commStyle || !groupOri) {
      window.alert(`Template "${template.id}" is missing required experimental factors. Expected agents.communicationStyle and agents.groupFocus.`);
      return;
    }

    const proProportion = template.agents?.stanceDistribution?.pro ?? 0.5;

    setFormData({
      id: template.id,
      name: template.name,
      description: template.description,
      topic: {
        name: shared.topic.name,
        description: shared.topic.description,
        proPosition: shared.topic.proPosition,
        contraPosition: shared.topic.contraPosition,
        hashtags: shared.topic.hashtags || [],
        talkingPoints: shared.topic.talkingPoints || [],
      },
      agents: {
        count: shared.agents?.count || 12,
        communicationStyle: commStyle,
        groupFocus: groupOri,
        stanceDistribution: {
          pro: proProportion,
          contra: 1 - proProportion,
        },
        emotionalFrames: {
          proStance: ["bonding", "hope", "validation"],
          contraStance: ["outrage", "fear", "concern"],
        },
        emotionalIntensity: 0.7,
        nameStyle: shared.agents?.nameStyle || "realistic",
        generateBios: shared.agents?.generateBios !== false,
        generateAvatars: shared.agents?.generateAvatars !== false,
      },
      timing: {
        prePopulationPosts: shared.timing?.prePopulationPosts || 20,
        prePopulationComments: shared.timing?.prePopulationComments || 30,
        postingIntervalMin: shared.timing?.postingIntervalMin || 8,
        postingIntervalMax: shared.timing?.postingIntervalMax || 15,
        reactionDelayMin: shared.timing?.reactionDelayMin || 10,
        reactionDelayMax: shared.timing?.reactionDelayMax || 120,
        reactionProbability: shared.timing?.reactionProbability || 0.4,
        commentProbability: shared.timing?.commentProbability || 0.6,
      },
      session: {
        durationMinutes: shared.session?.durationMinutes || 0,
        warningMinutes: shared.session?.warningMinutes || 5,
      },
      completionRedirect: {
        url: shared.completionRedirect?.url || "",
        participantIdParam: shared.completionRedirect?.participantIdParam || "i",
        includeParticipantId: shared.completionRedirect?.includeParticipantId !== false,
      },
      onboarding: {
        seedStatements: template.onboarding?.seedStatements || [],
        digestConfig: template.onboarding?.digestConfig || { maxExchanges: 3, preferFollowed: true },
        firstPostPrompt: template.onboarding?.firstPostPrompt || "",
      },
      experimentalFactors: [
        {
          name: "Communication Style",
          urlParam: "commStyle",
          values: [commStyle],
          description: "Agent communication style for this condition"
        },
        {
          name: "Group Focus",
          urlParam: "groupFocus",
          values: [groupOri],
          description: "Agent group focus for this condition"
        },
      ],
    });

    setSelectedTemplate(templateId);
  };

  useEffect(() => {
    if (condition) {
      setFormData({
        id: condition._id || "",
        name: condition.name || "",
        description: condition.description || "",
        topic: {
          name: condition.topic?.name || "",
          description: condition.topic?.description || "",
          proPosition: condition.topic?.proPosition || "",
          contraPosition: condition.topic?.contraPosition || "",
          hashtags: condition.topic?.hashtags || [],
          talkingPoints: condition.topic?.talkingPoints || [],
        },
        agents: {
          count: condition.agents?.count || 12,
          communicationStyle: condition.agents?.communicationStyle || "emotional",
          groupFocus: condition.agents?.groupFocus || "neutral",
          stanceDistribution: condition.agents?.stanceDistribution || {
            pro: 0.5,
            contra: 0.5,
          },
          emotionalFrames: condition.agents?.emotionalFrames || {
            proStance: ["bonding", "hope", "validation"],
            contraStance: ["outrage", "fear", "concern"],
          },
          emotionalIntensity: condition.agents?.emotionalIntensity ?? 0.7,
          nameStyle: condition.agents?.nameStyle || "realistic",
          generateBios: condition.agents?.generateBios !== false,
          generateAvatars: condition.agents?.generateAvatars !== false,
        },
        timing: {
          prePopulationPosts: condition.timing?.prePopulationPosts || 20,
          prePopulationComments: condition.timing?.prePopulationComments || 30,
          postingIntervalMin: condition.timing?.postingIntervalMin || 8,
          postingIntervalMax: condition.timing?.postingIntervalMax || 15,
          reactionDelayMin: condition.timing?.reactionDelayMin || 10,
          reactionDelayMax: condition.timing?.reactionDelayMax || 120,
          reactionProbability: condition.timing?.reactionProbability || 0.4,
          commentProbability: condition.timing?.commentProbability || 0.6,
        },
        session: {
          durationMinutes: condition.session?.durationMinutes || 0,
          warningMinutes: condition.session?.warningMinutes || 5,
        },
        completionRedirect: {
          url: condition.completionRedirect?.url || "",
          participantIdParam: condition.completionRedirect?.participantIdParam || "i",
          includeParticipantId: condition.completionRedirect?.includeParticipantId !== false,
        },
        experimentalFactors: condition.experimentalFactors || [],
        onboarding: {
          seedStatements: condition.onboarding?.seedStatements || [],
          digestConfig: condition.onboarding?.digestConfig || { maxExchanges: 3, preferFollowed: true },
          firstPostPrompt: condition.onboarding?.firstPostPrompt || "",
        },
      });
    }
  }, [condition]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.id.trim()) newErrors.id = "Condition ID is required";
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.topic.name.trim()) newErrors.topicName = "Topic name is required";

    // Validate stance distribution sums to 1.0
    const stanceSum =
      (parseFloat(formData.agents.stanceDistribution?.pro) || 0) +
      (parseFloat(formData.agents.stanceDistribution?.contra) || 0);
    if (Math.abs(stanceSum - 1.0) > 0.01) {
      newErrors.stanceDistribution = `Pro + Contra must sum to 1.0 (current: ${stanceSum.toFixed(2)})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const updateNestedField = (path, value) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const keys = path.split(".");
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const toggleEmotionalFrame = (stanceType, frame) => {
    const currentFrames = formData.agents.emotionalFrames[stanceType] || [];
    const newFrames = currentFrames.includes(frame)
      ? currentFrames.filter(f => f !== frame)
      : [...currentFrames, frame];
    updateNestedField(`agents.emotionalFrames.${stanceType}`, newFrames);
  };

  const addHashtag = () => {
    if (hashtagInput.trim()) {
      const tag = hashtagInput.trim().replace(/^#/, "");
      if (!formData.topic.hashtags.includes(tag)) {
        updateNestedField("topic.hashtags", [...formData.topic.hashtags, tag]);
      }
      setHashtagInput("");
    }
  };

  const removeHashtag = (tag) => {
    updateNestedField(
      "topic.hashtags",
      formData.topic.hashtags.filter((t) => t !== tag)
    );
  };

  const addExperimentalFactor = () => {
    updateNestedField("experimentalFactors", [
      ...formData.experimentalFactors,
      { name: "", urlParam: "", values: [], description: "" },
    ]);
  };

  const updateExperimentalFactor = (index, field, value) => {
    const newFactors = [...formData.experimentalFactors];
    newFactors[index] = { ...newFactors[index], [field]: value };
    updateNestedField("experimentalFactors", newFactors);
  };

  const removeExperimentalFactor = (index) => {
    updateNestedField(
      "experimentalFactors",
      formData.experimentalFactors.filter((_, i) => i !== index)
    );
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">
          {isNew ? "Create New Condition" : "Edit Condition"}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-white p-2"
        >
          <FaTimes size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Template Loader - Only show for new conditions */}
        {isNew && (
          <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg p-4 mb-4 border border-blue-500/30">
            <div className="flex items-center mb-3">
              <FaFileImport className="text-blue-400 mr-2" />
              <h3 className="text-lg font-semibold text-white">Load from Study Template</h3>
              <span className="ml-2 text-gray-400 text-sm">
                (2x3x2 Factorial Design - 12 conditions per topic)
              </span>
            </div>

            {/* Topic Selector */}
            <div className="mb-3">
              <label className="block text-gray-300 text-sm mb-1">1. Select Topic</label>
              <select
                value={selectedTopic}
                onChange={(e) => {
                  setSelectedTopic(e.target.value);
                  setSelectedTemplate(""); // Reset template when topic changes
                }}
                className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none"
              >
                <option value="">-- Select a topic --</option>
                {Object.entries(TOPIC_TEMPLATES).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Condition Template Selector - Only show when topic is selected */}
            {selectedTopic && currentTopicData && (
              <div className="mb-3">
                <label className="block text-gray-300 text-sm mb-1">2. Select Condition</label>
                <div className="flex gap-3">
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="flex-1 p-2 bg-gray-700 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">-- Select a condition --</option>
                    <optgroup label="Rational Communication">
                      {currentTopicData.templates
                        .filter(t => (t.agents?.communicationStyle || t.factors?.communicationStyle) === "rational")
                        .map(t => (
                          <option key={t.id} value={t.id}>
                            [{t.shortCode}] {t.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Emotional Communication">
                      {currentTopicData.templates
                        .filter(t => (t.agents?.communicationStyle || t.factors?.communicationStyle) === "emotional")
                        .map(t => (
                          <option key={t.id} value={t.id}>
                            [{t.shortCode}] {t.name}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                  <button
                    type="button"
                    onClick={() => loadTemplate(selectedTemplate)}
                    disabled={!selectedTemplate}
                    className={`px-4 py-2 rounded flex items-center ${
                      selectedTemplate
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-600 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <FaFileImport className="mr-2" /> Load Template
                  </button>
                </div>
              </div>
            )}

            {/* Show topic description */}
            {selectedTopic && currentTopicData && (
              <div className="bg-gray-800/50 p-3 rounded mt-2">
                <p className="text-white text-sm font-semibold mb-1">
                  {currentTopicData.sharedConfig.topic.name}
                </p>
                <p className="text-gray-400 text-xs line-clamp-3">
                  {currentTopicData.sharedConfig.topic.description}
                </p>
              </div>
            )}

            {/* Show selected template description */}
            {selectedTemplate && currentTopicData && (
              <p className="text-blue-300 text-sm mt-2">
                {currentTopicData.templates.find(t => t.id === selectedTemplate)?.description}
              </p>
            )}
          </div>
        )}

        {/* Basic Info */}
        <Section title="Basic Information">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Condition ID"
              value={formData.id}
              onChange={(v) => setFormData({ ...formData, id: v })}
              error={errors.id}
              placeholder="e.g., polarization_v1"
              disabled={!isNew}
            />
            <Input
              label="Display Name"
              value={formData.name}
              onChange={(v) => setFormData({ ...formData, name: v })}
              error={errors.name}
              placeholder="e.g., Polarization Study v1"
            />
          </div>
          <TextArea
            label="Description"
            value={formData.description}
            onChange={(v) => setFormData({ ...formData, description: v })}
            placeholder="Internal description of this condition..."
          />
        </Section>

        {/* Topic Configuration */}
        <Section title="Topic Configuration" info="The debate topic for this condition">
          <Input
            label="Topic Name"
            value={formData.topic.name}
            onChange={(v) => updateNestedField("topic.name", v)}
            error={errors.topicName}
            placeholder="e.g., AI Exam Monitoring"
          />
          <TextArea
            label="Topic Description"
            value={formData.topic.description}
            onChange={(v) => updateNestedField("topic.description", v)}
            placeholder="Detailed description of the topic for AI agents..."
            rows={4}
          />
          <div className="grid grid-cols-2 gap-4">
            <TextArea
              label="Pro Position"
              value={formData.topic.proPosition}
              onChange={(v) => updateNestedField("topic.proPosition", v)}
              placeholder="Arguments in favor of the topic..."
            />
            <TextArea
              label="Contra Position"
              value={formData.topic.contraPosition}
              onChange={(v) => updateNestedField("topic.contraPosition", v)}
              placeholder="Arguments against the topic..."
            />
          </div>
          <div className="mb-3">
            <label className="block text-gray-300 text-sm mb-1">Hashtags</label>
            <div className="flex gap-2 mb-2">
              <input
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addHashtag())}
                placeholder="Add hashtag..."
                className="flex-1 p-2 bg-gray-600 text-white rounded border border-gray-500"
              />
              <button
                type="button"
                onClick={addHashtag}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 rounded"
              >
                <FaPlus />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.topic.hashtags.map((tag) => (
                <span
                  key={tag}
                  className="bg-blue-600 text-white px-2 py-1 rounded text-sm flex items-center"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => removeHashtag(tag)}
                    className="ml-2 text-blue-200 hover:text-white"
                  >
                    <FaTimes size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Talking Points */}
          <div className="mb-3 mt-4">
            <label className="block text-gray-300 text-sm mb-2 font-semibold">
              Talking Points
              <span className="ml-2 font-normal text-gray-400">
                ({(formData.topic.talkingPoints || []).length} points)
              </span>
            </label>
            {(formData.topic.talkingPoints || []).map((tp, index) => (
              <div key={index} className="bg-gray-600 p-3 rounded mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">Point {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newPoints = (formData.topic.talkingPoints || []).filter((_, i) => i !== index);
                      updateNestedField("topic.talkingPoints", newPoints);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <FaTrash />
                  </button>
                </div>
                <input
                  value={tp.title}
                  onChange={(e) => {
                    const newPoints = [...(formData.topic.talkingPoints || [])];
                    newPoints[index] = { ...newPoints[index], title: e.target.value };
                    updateNestedField("topic.talkingPoints", newPoints);
                  }}
                  placeholder="Title (e.g., Iceland's national trial)"
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none mb-2"
                />
                <textarea
                  value={tp.description}
                  onChange={(e) => {
                    const newPoints = [...(formData.topic.talkingPoints || [])];
                    newPoints[index] = { ...newPoints[index], description: e.target.value };
                    updateNestedField("topic.talkingPoints", newPoints);
                  }}
                  placeholder="Description with specific facts, figures, or examples..."
                  rows={2}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none mb-2"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Stance</label>
                    <select
                      value={tp.stance || "neutral"}
                      onChange={(e) => {
                        const newPoints = [...(formData.topic.talkingPoints || [])];
                        newPoints[index] = { ...newPoints[index], stance: e.target.value };
                        updateNestedField("topic.talkingPoints", newPoints);
                      }}
                      className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500"
                    >
                      <option value="pro">Pro</option>
                      <option value="contra">Contra</option>
                      <option value="neutral">Neutral</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Type</label>
                    <select
                      value={tp.type || "argument"}
                      onChange={(e) => {
                        const newPoints = [...(formData.topic.talkingPoints || [])];
                        newPoints[index] = { ...newPoints[index], type: e.target.value };
                        updateNestedField("topic.talkingPoints", newPoints);
                      }}
                      className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500"
                    >
                      <option value="event">Event</option>
                      <option value="statistic">Statistic</option>
                      <option value="anecdote">Anecdote</option>
                      <option value="question">Question</option>
                      <option value="argument">Argument</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newPoints = [...(formData.topic.talkingPoints || []), { title: "", description: "", stance: "neutral", type: "argument" }];
                updateNestedField("topic.talkingPoints", newPoints);
              }}
              className="w-full py-2 border-2 border-dashed border-gray-500 text-gray-400 rounded hover:border-blue-500 hover:text-blue-400 flex items-center justify-center"
            >
              <FaPlus className="mr-2" /> Add Talking Point
            </button>
          </div>
        </Section>

        {/* Agent Configuration */}
        <Section title="Agent Configuration" info="Configure AI agent behavior (rigid per-condition)">
          <Input
            label="Number of Agents"
            type="number"
            value={formData.agents.count}
            onChange={(v) => updateNestedField("agents.count", parseInt(v) || 12)}
            min={1}
            max={50}
          />

          {/* Communication Style - Rigid selection */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2">
              Communication Style (all agents)
            </label>
            <div className="grid grid-cols-2 gap-4">
              {["emotional", "rational"].map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={() => updateNestedField("agents.communicationStyle", style)}
                  className={`p-3 rounded border-2 transition-colors ${
                    formData.agents.communicationStyle === style
                      ? "border-blue-500 bg-blue-600/30 text-white"
                      : "border-gray-500 bg-gray-600 text-gray-300 hover:border-gray-400"
                  }`}
                >
                  <span className="capitalize font-semibold">{style}</span>
                  <p className="text-xs mt-1 opacity-75">
                    {style === "emotional"
                      ? "Expressive, passionate language"
                      : "Logical, evidence-based arguments"}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Group Focus - Rigid selection */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2">
              Group Focus (all agents)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: "neutral", label: "Neutral", desc: "Topic-focused, no group identity" },
                { value: "ingroup", label: "In-group", desc: "Solidarity with like-minded" },
                { value: "outgroup", label: "Out-group", desc: "Criticism of opponents" },
              ].map(({ value, label, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => updateNestedField("agents.groupFocus", value)}
                  className={`p-3 rounded border-2 transition-colors ${
                    formData.agents.groupFocus === value
                      ? "border-blue-500 bg-blue-600/30 text-white"
                      : "border-gray-500 bg-gray-600 text-gray-300 hover:border-gray-400"
                  }`}
                >
                  <span className="font-semibold">{label}</span>
                  <p className="text-xs mt-1 opacity-75">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Stance Distribution */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2">
              Stance Distribution
              {errors.stanceDistribution && (
                <span className="text-red-400 ml-2">{errors.stanceDistribution}</span>
              )}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-600 p-3 rounded">
                <div className="flex justify-between text-white mb-2">
                  <span>Pro</span>
                  <span>{((formData.agents.stanceDistribution?.pro || 0) * 100).toFixed(0)}%</span>
                </div>
                <input
                  type="range"
                  value={formData.agents.stanceDistribution?.pro || 0.5}
                  onChange={(e) => {
                    const pro = parseFloat(e.target.value);
                    updateNestedField("agents.stanceDistribution", { pro, contra: 1 - pro });
                  }}
                  min={0}
                  max={1}
                  step={0.05}
                  className="w-full"
                />
              </div>
              <div className="bg-gray-600 p-3 rounded">
                <div className="flex justify-between text-white mb-2">
                  <span>Contra</span>
                  <span>{((formData.agents.stanceDistribution?.contra || 0) * 100).toFixed(0)}%</span>
                </div>
                <input
                  type="range"
                  value={formData.agents.stanceDistribution?.contra || 0.5}
                  onChange={(e) => {
                    const contra = parseFloat(e.target.value);
                    updateNestedField("agents.stanceDistribution", { pro: 1 - contra, contra });
                  }}
                  min={0}
                  max={1}
                  step={0.05}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Emotional Frames (only shown for emotional style) */}
          {formData.agents.communicationStyle === "emotional" && (
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">
                Emotional Frames (pools for random assignment)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-600 p-3 rounded">
                  <p className="text-white text-sm font-semibold mb-2">Pro-stance frames:</p>
                  <div className="flex flex-wrap gap-2">
                    {["bonding", "hope", "validation", "pride"].map((frame) => (
                      <button
                        key={frame}
                        type="button"
                        onClick={() => toggleEmotionalFrame("proStance", frame)}
                        className={`px-2 py-1 rounded text-sm ${
                          formData.agents.emotionalFrames?.proStance?.includes(frame)
                            ? "bg-green-600 text-white"
                            : "bg-gray-500 text-gray-300"
                        }`}
                      >
                        {frame}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-600 p-3 rounded">
                  <p className="text-white text-sm font-semibold mb-2">Contra-stance frames:</p>
                  <div className="flex flex-wrap gap-2">
                    {["outrage", "fear", "concern", "frustration"].map((frame) => (
                      <button
                        key={frame}
                        type="button"
                        onClick={() => toggleEmotionalFrame("contraStance", frame)}
                        className={`px-2 py-1 rounded text-sm ${
                          formData.agents.emotionalFrames?.contraStance?.includes(frame)
                            ? "bg-red-600 text-white"
                            : "bg-gray-500 text-gray-300"
                        }`}
                      >
                        {frame}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <RangeInput
                label="Emotional Intensity"
                value={formData.agents.emotionalIntensity ?? 0.7}
                onChange={(v) => updateNestedField("agents.emotionalIntensity", v)}
                min={0}
                max={1}
              />
            </div>
          )}

          <div className="flex gap-4 mt-3">
            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={formData.agents.generateBios}
                onChange={(e) => updateNestedField("agents.generateBios", e.target.checked)}
                className="mr-2"
              />
              Generate Bios
            </label>
            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={formData.agents.generateAvatars}
                onChange={(e) => updateNestedField("agents.generateAvatars", e.target.checked)}
                className="mr-2"
              />
              Generate Avatars
            </label>
          </div>
        </Section>

        {/* Timing Configuration */}
        <Section title="Timing Configuration" info="Control content generation timing">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Pre-population Posts"
              type="number"
              value={formData.timing.prePopulationPosts}
              onChange={(v) => updateNestedField("timing.prePopulationPosts", parseInt(v) || 0)}
            />
            <Input
              label="Pre-population Comments"
              type="number"
              value={formData.timing.prePopulationComments}
              onChange={(v) => updateNestedField("timing.prePopulationComments", parseInt(v) || 0)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Posting Interval Min (minutes)"
              type="number"
              value={formData.timing.postingIntervalMin}
              onChange={(v) => updateNestedField("timing.postingIntervalMin", parseInt(v) || 0)}
            />
            <Input
              label="Posting Interval Max (minutes)"
              type="number"
              value={formData.timing.postingIntervalMax}
              onChange={(v) => updateNestedField("timing.postingIntervalMax", parseInt(v) || 0)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <RangeInput
              label="Reaction Probability"
              value={formData.timing.reactionProbability}
              onChange={(v) => updateNestedField("timing.reactionProbability", v)}
              min={0}
              max={1}
            />
            <RangeInput
              label="Comment Probability (vs Like)"
              value={formData.timing.commentProbability}
              onChange={(v) => updateNestedField("timing.commentProbability", v)}
              min={0}
              max={1}
            />
          </div>
        </Section>

        {/* Session Configuration */}
        <Section title="Session Configuration" info="Optional time limits for participant sessions">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Session Duration (minutes)"
              type="number"
              value={formData.session.durationMinutes}
              onChange={(v) => updateNestedField("session.durationMinutes", parseInt(v) || 0)}
              placeholder="0 = unlimited"
              min={0}
            />
            <Input
              label="Warning Before End (minutes)"
              type="number"
              value={formData.session.warningMinutes}
              onChange={(v) => updateNestedField("session.warningMinutes", parseInt(v) || 5)}
              min={0}
            />
          </div>
          <p className="text-gray-400 text-xs mt-2">
            Set duration to 0 for unlimited sessions. Warning will show a notification before the session ends.
          </p>
        </Section>

        {/* Onboarding Configuration */}
        <Section title="Onboarding Configuration" info="Seed statements and digest settings for onboarding flow">
          {/* Seed Statements */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2 font-semibold">
              Seed Statements
            </label>
            {(formData.onboarding?.seedStatements || []).map((stmt, index) => (
              <div key={index} className="bg-gray-600 p-3 rounded mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white font-medium">Statement {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newStatements = (formData.onboarding?.seedStatements || []).filter((_, i) => i !== index);
                      updateNestedField("onboarding.seedStatements", newStatements);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <FaTrash />
                  </button>
                </div>
                <textarea
                  value={stmt.text}
                  onChange={(e) => {
                    const newStatements = [...(formData.onboarding?.seedStatements || [])];
                    newStatements[index] = { ...newStatements[index], text: e.target.value };
                    updateNestedField("onboarding.seedStatements", newStatements);
                  }}
                  placeholder="Enter seed statement text..."
                  rows={2}
                  className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none mb-2"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Stance</label>
                    <select
                      value={stmt.stance || "pro"}
                      onChange={(e) => {
                        const newStatements = [...(formData.onboarding?.seedStatements || [])];
                        newStatements[index] = { ...newStatements[index], stance: e.target.value };
                        updateNestedField("onboarding.seedStatements", newStatements);
                      }}
                      className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500"
                    >
                      <option value="pro">Pro</option>
                      <option value="contra">Contra</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Style</label>
                    <select
                      value={stmt.style || "emotional"}
                      onChange={(e) => {
                        const newStatements = [...(formData.onboarding?.seedStatements || [])];
                        newStatements[index] = { ...newStatements[index], style: e.target.value };
                        updateNestedField("onboarding.seedStatements", newStatements);
                      }}
                      className="w-full p-2 bg-gray-700 text-white rounded border border-gray-500"
                    >
                      <option value="emotional">Emotional</option>
                      <option value="rational">Rational</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newStatements = [...(formData.onboarding?.seedStatements || []), { text: "", stance: "pro", style: formData.agents.communicationStyle || "emotional" }];
                updateNestedField("onboarding.seedStatements", newStatements);
              }}
              className="w-full py-2 border-2 border-dashed border-gray-500 text-gray-400 rounded hover:border-blue-500 hover:text-blue-400 flex items-center justify-center"
            >
              <FaPlus className="mr-2" /> Add Statement
            </button>
          </div>

          {/* Digest Configuration */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2 font-semibold">
              Digest Configuration
            </label>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Max Exchanges"
                type="number"
                value={formData.onboarding?.digestConfig?.maxExchanges ?? 3}
                onChange={(v) => updateNestedField("onboarding.digestConfig.maxExchanges", parseInt(v) || 3)}
                min={1}
                max={5}
              />
              <div className="mb-3 flex items-center pt-6">
                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={formData.onboarding?.digestConfig?.preferFollowed !== false}
                    onChange={(e) => updateNestedField("onboarding.digestConfig.preferFollowed", e.target.checked)}
                    className="mr-2"
                  />
                  Prefer Followed Users
                </label>
              </div>
            </div>
          </div>

          {/* First Post Prompt */}
          <TextArea
            label="First Post Prompt"
            value={formData.onboarding?.firstPostPrompt || ""}
            onChange={(v) => updateNestedField("onboarding.firstPostPrompt", v)}
            placeholder="e.g., What's been on your mind about this? Share how you feel."
            rows={2}
          />
        </Section>

        {/* Completion Redirect */}
        <Section title="Survey Completion Redirect" info="Configure redirect URL for SoSciSurvey">
          <Input
            label="Redirect URL"
            value={formData.completionRedirect.url}
            onChange={(v) => updateNestedField("completionRedirect.url", v)}
            placeholder="e.g., https://www.soscisurvey.de/your-survey/"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Participant ID Parameter"
              value={formData.completionRedirect.participantIdParam}
              onChange={(v) => updateNestedField("completionRedirect.participantIdParam", v)}
              placeholder="i"
            />
            <div className="mb-3 flex items-center pt-6">
              <label className="flex items-center text-gray-300">
                <input
                  type="checkbox"
                  checked={formData.completionRedirect.includeParticipantId}
                  onChange={(e) => updateNestedField("completionRedirect.includeParticipantId", e.target.checked)}
                  className="mr-2"
                />
                Include Participant ID in URL
              </label>
            </div>
          </div>
          <p className="text-gray-400 text-xs mt-2">
            The redirect URL will be: {formData.completionRedirect.url || "(not set)"}
            {formData.completionRedirect.url && formData.completionRedirect.includeParticipantId && (
              <>?{formData.completionRedirect.participantIdParam || "i"}=PARTICIPANT_ID</>
            )}
          </p>
        </Section>

        {/* Experimental Factors */}
        <Section title="Experimental Factors" info="URL parameters from SoSciSurvey">
          {formData.experimentalFactors.map((factor, index) => (
            <div key={index} className="bg-gray-600 p-3 rounded mb-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white font-medium">Factor {index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeExperimentalFactor(index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <FaTrash />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  placeholder="Factor name (e.g., involvement)"
                  value={factor.name}
                  onChange={(e) => updateExperimentalFactor(index, "name", e.target.value)}
                  className="p-2 bg-gray-700 text-white rounded border border-gray-500"
                />
                <input
                  placeholder="URL param (e.g., inv)"
                  value={factor.urlParam}
                  onChange={(e) => updateExperimentalFactor(index, "urlParam", e.target.value)}
                  className="p-2 bg-gray-700 text-white rounded border border-gray-500"
                />
              </div>
              <input
                placeholder="Values (comma-separated, e.g., high,low)"
                value={factor.values?.join(",") || ""}
                onChange={(e) =>
                  updateExperimentalFactor(
                    index,
                    "values",
                    e.target.value.split(",").map((v) => v.trim())
                  )
                }
                className="w-full mt-2 p-2 bg-gray-700 text-white rounded border border-gray-500"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addExperimentalFactor}
            className="w-full py-2 border-2 border-dashed border-gray-500 text-gray-400 rounded hover:border-blue-500 hover:text-blue-400 flex items-center justify-center"
          >
            <FaPlus className="mr-2" /> Add Experimental Factor
          </button>
        </Section>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center"
          >
            <FaSave className="mr-2" /> {isNew ? "Create Condition" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConditionEditor;
